/**
 * One acute case in the drawer: the phone assistant's record, the note
 * thread, and whichever action the logged-in role is allowed to take —
 * ordinace writes a note, recepce closes the case.
 */

import { Check, Clock, Phone, User } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Textarea } from '@/components/ui/textarea'
import type { DeskRole } from '@/lib/desk-session-storage'
import { cn } from '@/utils/misc'
import { type DeskNote, NoteThread } from './NoteThread'

export interface DeskAcuteCase {
	id: string
	jmeno: string
	prijmeni: string
	telefon: string
	problem: string
	strana: string | null
	poziceZubu: string | null
	trvani: string | null
	citlivost: string | null
	leky: string | null
	zavaznost: string
	urgent: boolean
	poznamka: string | null
	status: 'new' | 'in_progress' | 'done'
	createdAt: string
	patient: { id: string; firstName: string; lastName: string } | null
	notes: DeskNote[]
}

/** 777123456 → 777 123 456 (spacing used across the app for CZ numbers). */
function formatPhone(telefon: string): string {
	return telefon.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')
}

function formatCreatedAt(iso: string): string {
	const date = new Date(iso)
	const hours = date.getHours().toString().padStart(2, '0')
	const minutes = date.getMinutes().toString().padStart(2, '0')
	return `${date.getDate()}. ${date.getMonth() + 1}. ${date.getFullYear()} ${hours}:${minutes}`
}

/** Label/value row, rendered only when the agent captured the field. */
function Detail({ label, value }: { label: string; value: string | null }) {
	if (!value) return null
	return (
		<div className="flex gap-2 text-[13px]">
			<span className="shrink-0 text-[#8c8275]">{label}</span>
			<span className="break-words text-[#3d3428]">{value}</span>
		</div>
	)
}

interface AcuteCaseDeskCardProps {
	item: DeskAcuteCase
	role: DeskRole
	onAddNote: (caseId: string, text: string) => Promise<boolean>
	onResolve: (caseId: string) => void
	isSaving: boolean
	isResolving: boolean
}

export function AcuteCaseDeskCard({
	item,
	role,
	onAddNote,
	onResolve,
	isSaving,
	isResolving,
}: AcuteCaseDeskCardProps) {
	const [draft, setDraft] = useState('')

	const toothDetails = [
		item.strana,
		item.poziceZubu ? `zub ${item.poziceZubu}` : null,
	]
		.filter(Boolean)
		.join(', ')

	const handleSave = async () => {
		const text = draft.trim()
		if (!text || isSaving) return
		// The draft is only cleared once the server has the note — a failed
		// save must not throw away what the doctor typed.
		const saved = await onAddNote(item.id, text)
		if (saved) setDraft('')
	}

	return (
		<article
			className={cn(
				'rounded-lg border bg-white p-4 shadow-sm',
				item.urgent ? 'border-red-300 bg-red-50/40' : 'border-[#e8e4df]',
			)}
		>
			<header className="flex flex-wrap items-start justify-between gap-2">
				<div className="min-w-0">
					<h3 className="font-semibold text-[#3d3428]">
						{item.jmeno} {item.prijmeni}
					</h3>
					<p className="mt-0.5 flex items-center gap-1.5 text-[13px] text-[#6b5c4c]">
						<Phone className="h-3.5 w-3.5 text-[#a09689]" />
						<a
							href={`tel:+420${item.telefon}`}
							className="hover:text-[#3d3428] hover:underline"
						>
							{formatPhone(item.telefon)}
						</a>
					</p>
				</div>
				{item.urgent && (
					<span className="rounded-full bg-red-600 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
						Urgentní
					</span>
				)}
			</header>

			<p className="mt-3 text-sm font-medium text-[#3d3428]">{item.problem}</p>

			<div className="mt-2 space-y-1">
				<Detail label="Zub:" value={toothDetails || null} />
				<Detail label="Trvání:" value={item.trvani} />
				<Detail label="Citlivost:" value={item.citlivost} />
				<Detail label="Léky:" value={item.leky} />
				<Detail label="Závažnost:" value={item.zavaznost} />
				<Detail label="Poznámka:" value={item.poznamka} />
			</div>

			<div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-[#8c8275]">
				<span className="flex items-center gap-1">
					<Clock className="h-3.5 w-3.5" />
					{formatCreatedAt(item.createdAt)}
				</span>
				{item.patient && (
					<Link
						to={`/patient/${item.patient.id}`}
						className="flex items-center gap-1 text-[#6b5c4c] hover:text-[#3d3428] hover:underline"
					>
						<User className="h-3.5 w-3.5" />
						{item.patient.firstName} {item.patient.lastName}
					</Link>
				)}
			</div>

			<div className="mt-4 border-t border-[#e8e4df] pt-3">
				<p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#8c8275]">
					Poznámky ordinace
				</p>
				<NoteThread notes={item.notes} />
			</div>

			{role === 'ordinace' && (
				<div className="mt-3 flex items-start gap-2">
					<Textarea
						rows={3}
						value={draft}
						onChange={(e) => setDraft(e.target.value)}
						placeholder="Poznámka pro recepci…"
						disabled={isSaving}
						// Explicit colours rather than the `bg-background` token: the
						// app boots with data-theme="dark" (contexts/theme-context.tsx
						// defaults to 'dark'), and DaisyUI 5's dark themes set
						// `color-scheme: dark`, which renders this control dark against
						// the drawer's hardcoded light palette.
						className="min-h-0 flex-1 resize-y border-[#ddd5c8] bg-white text-[#3d3428] placeholder:text-[#a09689] text-sm focus-visible:border-[#d7ad6c]"
					/>
					<button
						type="button"
						onClick={handleSave}
						disabled={!draft.trim() || isSaving}
						className="inline-flex h-10 shrink-0 items-center rounded-lg bg-[#d7ad6c] px-4 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#c49b5a] disabled:cursor-not-allowed disabled:opacity-50"
					>
						{isSaving ? 'Ukládám…' : 'Uložit'}
					</button>
				</div>
			)}

			{role === 'recepce' && (
				<button
					type="button"
					onClick={() => onResolve(item.id)}
					disabled={isResolving}
					className="mt-3 inline-flex h-10 items-center rounded-lg border border-[#ddd5c8] bg-white px-4 text-sm font-medium text-[#5c5142] transition-colors hover:border-[#d7ad6c] hover:bg-[#f5ebe0] disabled:cursor-not-allowed disabled:opacity-50"
				>
					<Check className="mr-2 h-4 w-4 text-[#a09689]" />
					Označit jako vyřešené
				</button>
			)}
		</article>
	)
}
